import { DashboardLayout } from '@/components/layout/DashboardLayout';
import AIAnalystPanel from '@/components/ai/AIAnalystPanel';
import { useState, useEffect, useCallback } from 'react';
import { useProject } from '@/contexts/ProjectContext';
import { useSafetyAlerts } from '@/contexts/SafetyAlertContext';
import { PermissionGate } from '@/components/PermissionGate';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Camera, Video, Shield, Settings, BarChart3, Map, HardDrive,
  RefreshCw, Plus, Filter, Maximize2, Grid3X3,
  AlertTriangle, CheckCircle, XCircle, Wifi, WifiOff, Zap, Clock,
  Crosshair, RotateCw, ArrowUp, ArrowDown,
  ArrowLeft, ArrowRight, ZoomIn, ZoomOut, Trash2, Edit2, X, Radio
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import ModuleInsightsDashboard from '@/components/common/ModuleInsightsDashboard';

const TABS = [
  { id: 'live', label: 'Live View', icon: Video },
  { id: 'insights', label: 'AI Insights', icon: BarChart3 },
  { id: 'map', label: 'Camera Map', icon: Map },
  { id: 'events', label: 'Safety Events', icon: Shield },
  { id: 'recordings', label: 'Recordings', icon: HardDrive },
  { id: 'config', label: 'Camera Config', icon: Settings },
  { id: 'ai', label: 'AI Settings', icon: Zap },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
];

const GRID_OPTIONS = ['1x1', '2x2', '3x3', '4x4'];
const SEV = {
  CRITICAL: { bg: 'rgba(239,68,68,0.15)', text: '#f87171', border: 'rgba(239,68,68,0.3)' },
  HIGH: { bg: 'rgba(249,115,22,0.15)', text: '#fb923c', border: 'rgba(249,115,22,0.3)' },
  MEDIUM: { bg: 'rgba(234,179,8,0.15)', text: '#facc15', border: 'rgba(234,179,8,0.3)' },
  LOW: { bg: 'rgba(59,130,246,0.15)', text: '#60a5fa', border: 'rgba(59,130,246,0.3)' },
};
const EVT_ST = {
  NEW: { bg: 'rgba(239,68,68,0.15)', text: '#f87171' },
  ACKNOWLEDGED: { bg: 'rgba(59,130,246,0.15)', text: '#60a5fa' },
  ACTION_TAKEN: { bg: 'rgba(16,185,129,0.15)', text: '#34d399' },
  FALSE_POSITIVE: { bg: 'rgba(100,116,139,0.15)', text: '#94a3b8' },
  CLOSED: { bg: 'rgba(100,116,139,0.15)', text: '#cbd5e1' },
  INVESTIGATING: { bg: 'rgba(234,179,8,0.15)', text: '#facc15' },
};
const STATUS_DOT = { ONLINE: '#10b981', OFFLINE: '#ef4444', ERROR: '#f59e0b', CONNECTING: '#3b82f6', MAINTENANCE: '#64748b' };

// Camera feed images mapped by area
const FEED_IMAGES = {
  AREA_1: 'https://images.unsplash.com/photo-1504687515379-dc34f9d74229?w=640&q=60&fit=crop',
  AREA_2: 'https://images.unsplash.com/photo-1630522521616-b5b4cf458621?w=640&q=60&fit=crop',
  AREA_3: 'https://images.unsplash.com/photo-1581094271901-8022df4466f9?w=640&q=60&fit=crop',
  MAIN_GATE: 'https://images.unsplash.com/photo-1546081090-660db58799a9?w=640&q=60&fit=crop',
  STORAGE_YARD: 'https://images.unsplash.com/photo-1527335988388-b40ee248d80c?w=640&q=60&fit=crop',
  PERIMETER: 'https://images.unsplash.com/photo-1747573235085-aa6b21b92e07?w=640&q=60&fit=crop',
  OFFICE: 'https://images.unsplash.com/photo-1766524554968-c8374b7d7355?w=640&q=60&fit=crop',
};
const DEFAULT_FEED = 'https://images.unsplash.com/photo-1504687515379-dc34f9d74229?w=640&q=60&fit=crop';

// Theme-aware style helpers
const card = () => ({ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' });
const cardAlt = () => ({ backgroundColor: 'var(--card-bg-alt)', border: '1px solid var(--border-color)' });
const inp = () => ({ backgroundColor: 'var(--input-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' });

export default function SiteMonitoring() {
  const { selectedProject } = useProject();
  const safetyAlerts = useSafetyAlerts();
  const projectId = selectedProject?.project_id;
  const [activeTab, setActiveTab] = useState('live');
  const [cameras, setCameras] = useState([]);
  const [health, setHealth] = useState(null);
  const [layouts, setLayouts] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventsSummary, setEventsSummary] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLayout, setSelectedLayout] = useState(null);
  const [gridSize, setGridSize] = useState('2x2');
  const [expandedCamera, setExpandedCamera] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [editingCamera, setEditingCamera] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventFilter, setEventFilter] = useState({ severity: '', status: '', type: '' });
  const [areaFilter, setAreaFilter] = useState('');

  const fetchAll = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [camRes, healthRes, layoutRes, evtRes, sumRes] = await Promise.all([
        api.get(`/projects/${projectId}/sitemon/cameras`),
        api.get(`/projects/${projectId}/sitemon/cameras/health`),
        api.get(`/projects/${projectId}/sitemon/layouts`),
        api.get(`/projects/${projectId}/sitemon/safety-events?limit=100`),
        api.get(`/projects/${projectId}/sitemon/safety-events/summary`),
      ]);
      setCameras(camRes.data.cameras || []);
      setHealth(healthRes.data);
      setLayouts(layoutRes.data.layouts || []);
      setEvents(evtRes.data.events || []);
      setEventsSummary(sumRes.data);
      const def = (layoutRes.data.layouts || []).find(l => l.isDefault);
      if (def) { setSelectedLayout(def); setGridSize(def.gridSize || '2x2'); }
    } catch (err) {
      if (err.response?.status !== 401) toast.error('Failed to load site monitoring data');
    } finally { setLoading(false); }
  }, [projectId]);

  const fetchAnalytics = useCallback(async () => {
    if (!projectId) return;
    try { const res = await api.get(`/projects/${projectId}/sitemon/safety-events/analytics?days=30`); setAnalytics(res.data); } catch {}
  }, [projectId]);

  const fetchRecordings = useCallback(async () => {
    if (!projectId) return;
    try { const res = await api.get(`/projects/${projectId}/sitemon/recordings?limit=100`); setRecordings(res.data.recordings || []); } catch {}
  }, [projectId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { if (activeTab === 'analytics') fetchAnalytics(); }, [activeTab, fetchAnalytics]);
  useEffect(() => { if (activeTab === 'recordings') fetchRecordings(); }, [activeTab, fetchRecordings]);

  // Refresh when WebSocket delivers new alerts
  useEffect(() => {
    if (safetyAlerts?.alerts?.length > 0) { fetchAll(); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safetyAlerts?.alerts?.length]);

  const seedData = async () => {
    try { const res = await api.post(`/projects/${projectId}/sitemon/seed`); toast.success(res.data.message); if (res.data.seeded) fetchAll(); } catch { toast.error('Seed failed'); }
  };
  const simulateDetection = async () => {
    try { const res = await api.post(`/projects/${projectId}/sitemon/ai/simulate-detection`); toast.success(`Simulated: ${res.data.detection?.detectionType || 'Detection'} on ${res.data.cameraCode}`); } catch (err) { toast.error(err.response?.data?.detail || 'Simulation failed'); }
  };
  const acknowledgeEvent = async (n) => {
    try { await api.put(`/projects/${projectId}/sitemon/safety-events/${n}/acknowledge`, { acknowledgedBy: 'HSE Officer' }); toast.success('Event acknowledged'); fetchAll(); } catch { toast.error('Failed'); }
  };
  const markFalsePositive = async (n) => {
    try { await api.put(`/projects/${projectId}/sitemon/safety-events/${n}/false-positive`, { reason: 'Manually reviewed' }); toast.success('Marked as false positive'); fetchAll(); } catch { toast.error('Failed'); }
  };
  const createHSEIncident = async (n) => {
    try { const res = await api.post(`/projects/${projectId}/sitemon/safety-events/${n}/create-hse-incident`); toast.success(`HSE Incident ${res.data.incidentNumber} created`); fetchAll(); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const areas = [...new Set(cameras.map(c => c.area))].sort();
  const gridCells = parseInt(gridSize[0]) * parseInt(gridSize[2]);
  const layoutCameras = selectedLayout?.positions?.map(p => cameras.find(c => c.cameraCode === p.cameraCode)).filter(Boolean) || [];
  const displayCameras = layoutCameras.length > 0 ? layoutCameras.slice(0, gridCells) : cameras.slice(0, gridCells);
  const filteredEvents = events.filter(e => {
    if (eventFilter.severity && e.detection?.severity !== eventFilter.severity) return false;
    if (eventFilter.status && e.response?.status !== eventFilter.status) return false;
    if (eventFilter.type && e.detection?.detectionType !== eventFilter.type) return false;
    if (areaFilter && e.area !== areaFilter) return false;
    return true;
  });

  if (!projectId) return (<DashboardLayout><div className="flex items-center justify-center h-64" style={{ color: 'var(--text-muted)' }}>Select a project to view Site Monitoring</div></DashboardLayout>);

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="site-monitoring-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Site Monitoring — CCTV & AI Safety</h1>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{selectedProject?.name} — {cameras.length} cameras registered</p>
          </div>
          <div className="flex items-center gap-2">
            {health && (
              <div className="flex items-center gap-3 text-xs mr-3">
                <span className="flex items-center gap-1" style={{ color: 'var(--text-secondary)' }}><span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#10b981' }} />{health.online} Online</span>
                <span className="flex items-center gap-1" style={{ color: 'var(--text-secondary)' }}><span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#ef4444' }} />{health.offline} Offline</span>
                <span className="flex items-center gap-1" style={{ color: 'var(--text-secondary)' }}><Zap className="w-3 h-3" style={{ color: '#06b6d4' }} />{health.aiActive} AI</span>
              </div>
            )}
            {/* WebSocket indicator */}
            <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: safetyAlerts?.connected ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)', color: safetyAlerts?.connected ? '#34d399' : '#f87171' }}>
              <Radio className="w-2.5 h-2.5" />{safetyAlerts?.connected ? 'WS Live' : 'WS Off'}
            </span>
            <Button size="sm" variant="outline" onClick={simulateDetection} data-testid="simulate-detection-btn" className="text-xs">
              <AlertTriangle className="w-3 h-3 mr-1" />Simulate AI Alert
            </Button>
            {cameras.length === 0 && (
              <Button size="sm" onClick={seedData} data-testid="seed-sitemon-btn" className="text-xs" style={{ backgroundColor: '#0891b2' }}>
                <Plus className="w-3 h-3 mr-1" />Seed Demo Data
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={fetchAll}><RefreshCw className="w-3.5 h-3.5" /></Button>
          </div>
        </div>

        {/* AI Analyst Panel */}
        {projectId && (
          <AIAnalystPanel projectId={projectId} moduleKey="SITE_MONITORING" defaultOpen={false} compact={true} />
        )}

        {/* Tabs */}
        <div className="flex gap-1 rounded-lg p-1" style={cardAlt()} data-testid="sitemon-tabs">
          {TABS.map(t => (
            <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setActiveTab(t.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all"
              style={activeTab === t.id ? { backgroundColor: 'rgba(6,182,212,0.15)', color: '#06b6d4', border: '1px solid rgba(6,182,212,0.3)' } : { color: 'var(--text-muted)' }}>
              <t.icon className="w-3.5 h-3.5" />{t.label}
              {t.id === 'events' && eventsSummary?.newUnacknowledged > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold rounded-full" style={{ backgroundColor: '#ef4444', color: '#fff' }}>{eventsSummary.newUnacknowledged}</span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          <motion.div key={activeTab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
            {activeTab === 'live' && <LiveViewTab cameras={cameras} displayCameras={displayCameras} gridSize={gridSize} setGridSize={setGridSize} layouts={layouts} selectedLayout={selectedLayout} setSelectedLayout={setSelectedLayout} expandedCamera={expandedCamera} setExpandedCamera={setExpandedCamera} eventsSummary={eventsSummary} acknowledgeEvent={acknowledgeEvent} projectId={projectId} fetchAll={fetchAll} safetyAlerts={safetyAlerts} />}
            {activeTab === 'insights' && <ModuleInsightsDashboard projectId={projectId} moduleKey="site" color="#06B6D4" deepLinkBase="/project-controls/site-monitoring" />}
            {activeTab === 'map' && <CameraMapTab cameras={cameras} />}
            {activeTab === 'events' && <SafetyEventsTab events={filteredEvents} eventFilter={eventFilter} setEventFilter={setEventFilter} areaFilter={areaFilter} setAreaFilter={setAreaFilter} areas={areas} selectedEvent={selectedEvent} setSelectedEvent={setSelectedEvent} acknowledgeEvent={acknowledgeEvent} markFalsePositive={markFalsePositive} createHSEIncident={createHSEIncident} eventsSummary={eventsSummary} />}
            {activeTab === 'recordings' && <RecordingsTab recordings={recordings} cameras={cameras} />}
            {activeTab === 'config' && <CameraConfigTab cameras={cameras} projectId={projectId} fetchAll={fetchAll} showConfigModal={showConfigModal} setShowConfigModal={setShowConfigModal} editingCamera={editingCamera} setEditingCamera={setEditingCamera} />}
            {activeTab === 'ai' && <AISettingsTab cameras={cameras} projectId={projectId} fetchAll={fetchAll} />}
            {activeTab === 'analytics' && <AnalyticsTab analytics={analytics} eventsSummary={eventsSummary} health={health} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
}

/* ═══════════════════ LIVE VIEW TAB ═══════════════════ */

function LiveViewTab({ cameras, displayCameras, gridSize, setGridSize, layouts, selectedLayout, setSelectedLayout, expandedCamera, setExpandedCamera, eventsSummary, acknowledgeEvent, projectId, safetyAlerts }) {
  const cols = parseInt(gridSize[0]);
  if (expandedCamera) return <ExpandedCameraView camera={expandedCamera} onClose={() => setExpandedCamera(null)} projectId={projectId} />;

  return (
    <div className="space-y-3" data-testid="live-view-tab">
      {/* Toolbar */}
      <div className="flex items-center justify-between rounded-lg px-4 py-2" style={card()}>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <Grid3X3 className="w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Grid:</span>
            <div className="flex gap-1">
              {GRID_OPTIONS.map(g => (
                <button key={g} onClick={() => setGridSize(g)} data-testid={`grid-${g}`}
                  className="px-2 py-0.5 text-xs rounded transition-all"
                  style={gridSize === g ? { backgroundColor: '#0891b2', color: '#fff' } : { backgroundColor: 'var(--input-bg)', color: 'var(--text-muted)' }}>{g}</button>
              ))}
            </div>
          </div>
          <div className="w-px h-5" style={{ backgroundColor: 'var(--border-color)' }} />
          <div className="flex items-center gap-1.5">
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Preset:</span>
            <select value={selectedLayout?.layoutId || ''} onChange={e => { const l = layouts.find(l => l.layoutId === e.target.value); setSelectedLayout(l || null); if (l) setGridSize(l.gridSize || '2x2'); }}
              className="text-xs rounded px-2 py-1" style={inp()}>
              <option value="">All Cameras</option>
              {layouts.map(l => <option key={l.layoutId} value={l.layoutId}>{l.layoutName}</option>)}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {eventsSummary?.newUnacknowledged > 0 && (
            <span className="flex items-center gap-1" style={{ color: '#f87171' }}><AlertTriangle className="w-3 h-3" />{eventsSummary.newUnacknowledged} Active Alerts</span>
          )}
        </div>
      </div>

      {/* Real-time alert banner */}
      {safetyAlerts?.alerts?.length > 0 && safetyAlerts.alerts[0] && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-3 rounded-lg px-4 py-2"
          style={{ backgroundColor: SEV[safetyAlerts.alerts[0].detection?.severity]?.bg || 'rgba(239,68,68,0.15)', border: `1px solid ${SEV[safetyAlerts.alerts[0].detection?.severity]?.border || 'rgba(239,68,68,0.3)'}` }}>
          <Radio className="w-4 h-4 animate-pulse" style={{ color: SEV[safetyAlerts.alerts[0].detection?.severity]?.text || '#f87171' }} />
          <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>
            Latest: <span style={{ color: SEV[safetyAlerts.alerts[0].detection?.severity]?.text }}>{safetyAlerts.alerts[0].detection?.detectionType?.replace(/_/g, ' ')}</span>
            {' '}on {safetyAlerts.alerts[0].cameraCode} — {safetyAlerts.alerts[0].area} ({((safetyAlerts.alerts[0].detection?.confidence || 0) * 100).toFixed(0)}%)
          </span>
          <span className="ml-auto text-[10px]" style={{ color: 'var(--text-muted)' }}>{new Date(safetyAlerts.alerts[0].receivedAt || safetyAlerts.alerts[0].eventTimestamp).toLocaleTimeString()}</span>
        </motion.div>
      )}

      {/* Camera Grid */}
      <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }} data-testid="camera-grid">
        {displayCameras.map(cam => (
          <CameraCell key={cam.cameraCode} camera={cam} onExpand={() => setExpandedCamera(cam)} projectId={projectId} />
        ))}
        {displayCameras.length === 0 && (
          <div className="col-span-full flex items-center justify-center h-48 text-sm" style={{ color: 'var(--text-muted)' }}>No cameras to display. Seed demo data or add cameras.</div>
        )}
      </div>

      {/* Safety Event Ticker */}
      {eventsSummary?.recentEvents?.length > 0 && (
        <div className="rounded-lg p-3" style={card()} data-testid="event-ticker">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-3.5 h-3.5" style={{ color: '#06b6d4' }} />
            <span className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Recent Safety Events</span>
          </div>
          <div className="space-y-1 max-h-28 overflow-y-auto">
            {eventsSummary.recentEvents.slice(0, 5).map(evt => (
              <div key={evt.eventNumber} className="flex items-center justify-between text-xs py-1 px-2 rounded" style={{ backgroundColor: 'var(--input-bg)' }}>
                <div className="flex items-center gap-2">
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ backgroundColor: SEV[evt.detection?.severity]?.bg, color: SEV[evt.detection?.severity]?.text, border: `1px solid ${SEV[evt.detection?.severity]?.border}` }}>{evt.detection?.severity}</span>
                  <span style={{ color: 'var(--text-muted)' }}>{evt.eventTimestamp?.slice(11, 16)}</span>
                  <span style={{ color: 'var(--text-secondary)' }}>{evt.cameraCode}: {evt.detection?.detectionType?.replace(/_/g, ' ')}</span>
                </div>
                <div className="flex items-center gap-1">
                  {evt.response?.status === 'NEW' && (
                    <button onClick={() => acknowledgeEvent(evt.eventNumber)} className="px-2 py-0.5 text-[10px] rounded" style={{ backgroundColor: 'rgba(59,130,246,0.2)', color: '#60a5fa' }}>ACK</button>
                  )}
                  <span className="px-1.5 py-0.5 rounded text-[10px]" style={{ backgroundColor: EVT_ST[evt.response?.status]?.bg, color: EVT_ST[evt.response?.status]?.text }}>{evt.response?.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CameraCell({ camera, onExpand, projectId }) {
  const st = camera.status?.connectionStatus || 'OFFLINE';
  const aiOn = camera.aiConfig?.aiEnabled;
  const feedImg = FEED_IMAGES[camera.area] || DEFAULT_FEED;
  const [persons] = useState(() => st === 'ONLINE' ? Math.floor(Math.random() * 15 + 3) : 0);

  const takeSnapshot = async (e) => {
    e.stopPropagation();
    try { await api.post(`/projects/${projectId}/sitemon/cameras/${camera.cameraCode}/snapshot`); toast.success('Snapshot captured'); } catch { toast.error('Snapshot failed'); }
  };

  return (
    <div className="group rounded-lg overflow-hidden transition-all hover:ring-1 hover:ring-cyan-500/30" style={card()} data-testid={`camera-cell-${camera.cameraCode}`}>
      {/* Video Area */}
      <div className="relative aspect-video cursor-pointer overflow-hidden" onClick={onExpand}>
        {st === 'ONLINE' ? (
          <>
            <img src={feedImg} alt={camera.cameraName} className="w-full h-full object-cover" style={{ filter: 'brightness(0.85) contrast(1.1)' }} loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60" />
            {/* Scan line effect */}
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.03) 2px, rgba(0,0,0,0.03) 4px)' }} />
            {/* LIVE badge */}
            <div className="absolute top-2 left-2 flex items-center gap-1">
              <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ backgroundColor: 'rgba(220,38,38,0.85)', color: '#fff' }}>
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />LIVE
              </span>
            </div>
            {aiOn && (
              <div className="absolute top-2 right-2">
                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium flex items-center gap-1" style={{ backgroundColor: 'rgba(6,182,212,0.8)', color: '#fff' }}>
                  <Zap className="w-2.5 h-2.5" />AI
                </span>
              </div>
            )}
            {/* Timestamp */}
            <div className="absolute bottom-8 left-2 text-[9px] font-mono" style={{ color: 'rgba(255,255,255,0.7)' }}>
              {new Date().toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </div>
            {/* Person count */}
            {persons > 0 && (
              <div className="absolute bottom-8 right-2">
                <span className="px-1.5 py-0.5 rounded text-[10px]" style={{ backgroundColor: 'rgba(0,0,0,0.7)', color: '#e2e8f0' }}>{persons} persons</span>
              </div>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: 'var(--input-bg)' }}>
            <div className="text-center">
              <WifiOff className="w-8 h-8 mx-auto mb-1" style={{ color: 'rgba(239,68,68,0.5)' }} />
              <span className="text-[10px]" style={{ color: '#f87171' }}>{st}</span>
            </div>
          </div>
        )}
      </div>
      {/* Info Bar */}
      <div className="px-2 py-1.5 flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium truncate" style={{ color: 'var(--text-primary)' }}>{camera.cameraCode}</p>
          <p className="text-[10px] truncate" style={{ color: 'var(--text-muted)' }}>{camera.cameraName}</p>
        </div>
        <div className="flex items-center gap-1 ml-1">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_DOT[st] || '#64748b' }} title={st} />
          <button onClick={takeSnapshot} className="p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity" title="Snapshot"><Camera className="w-3 h-3" style={{ color: 'var(--text-muted)' }} /></button>
          <button onClick={onExpand} className="p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity" title="Expand"><Maximize2 className="w-3 h-3" style={{ color: 'var(--text-muted)' }} /></button>
        </div>
      </div>
    </div>
  );
}

function ExpandedCameraView({ camera, onClose, projectId }) {
  const isPTZ = camera.hardware?.isPTZ;
  const feedImg = FEED_IMAGES[camera.area] || DEFAULT_FEED;
  return (
    <div className="space-y-3" data-testid="expanded-camera-view">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="p-1.5 rounded-lg" style={{ backgroundColor: 'var(--input-bg)' }}><X className="w-4 h-4" style={{ color: 'var(--text-secondary)' }} /></button>
          <div>
            <h2 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{camera.cameraCode} — {camera.cameraName}</h2>
            <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{camera.area} | {camera.hardware?.manufacturer} {camera.hardware?.model} | {camera.hardware?.resolution}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-2 py-0.5 rounded text-xs" style={{ backgroundColor: camera.status?.connectionStatus === 'ONLINE' ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)', color: camera.status?.connectionStatus === 'ONLINE' ? '#34d399' : '#f87171' }}>{camera.status?.connectionStatus}</span>
          {camera.aiConfig?.aiEnabled && <span className="px-2 py-0.5 rounded text-xs" style={{ backgroundColor: 'rgba(6,182,212,0.15)', color: '#22d3ee' }}>AI Active</span>}
        </div>
      </div>
      <div className="grid grid-cols-4 gap-3">
        {/* Main Video */}
        <div className="col-span-3 aspect-video rounded-lg overflow-hidden relative" style={{ border: '1px solid var(--border-color)' }}>
          <img src={feedImg} alt={camera.cameraName} className="w-full h-full object-cover" style={{ filter: 'brightness(0.85) contrast(1.1)' }} />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/50" />
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.03) 2px, rgba(0,0,0,0.03) 4px)' }} />
          <div className="absolute top-3 left-3"><span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold" style={{ backgroundColor: 'rgba(220,38,38,0.85)', color: '#fff' }}><span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />LIVE</span></div>
          <div className="absolute bottom-3 left-3 text-xs font-mono" style={{ color: 'rgba(255,255,255,0.8)' }}>{camera.cameraCode} — {new Date().toLocaleString()}</div>
          <div className="absolute bottom-3 right-3 text-xs font-mono" style={{ color: 'rgba(255,255,255,0.7)' }}>{camera.hardware?.resolution} {camera.hardware?.fps || 25}fps {camera.hardware?.codec || 'H.265'}</div>
        </div>
        {/* Controls */}
        <div className="space-y-3">
          {isPTZ && (
            <div className="rounded-lg p-3" style={card()}>
              <h3 className="text-xs font-semibold mb-2 flex items-center gap-1" style={{ color: 'var(--text-secondary)' }}><Crosshair className="w-3 h-3" />PTZ Control</h3>
              <div className="grid grid-cols-3 gap-1 w-24 mx-auto mb-2">
                <div /><button className="p-1.5 rounded" style={{ backgroundColor: 'var(--input-bg)' }}><ArrowUp className="w-3 h-3 mx-auto" style={{ color: 'var(--text-secondary)' }} /></button><div />
                <button className="p-1.5 rounded" style={{ backgroundColor: 'var(--input-bg)' }}><ArrowLeft className="w-3 h-3 mx-auto" style={{ color: 'var(--text-secondary)' }} /></button>
                <button className="p-1.5 rounded" style={{ backgroundColor: '#0891b2' }}><RotateCw className="w-3 h-3 mx-auto text-white" /></button>
                <button className="p-1.5 rounded" style={{ backgroundColor: 'var(--input-bg)' }}><ArrowRight className="w-3 h-3 mx-auto" style={{ color: 'var(--text-secondary)' }} /></button>
                <div /><button className="p-1.5 rounded" style={{ backgroundColor: 'var(--input-bg)' }}><ArrowDown className="w-3 h-3 mx-auto" style={{ color: 'var(--text-secondary)' }} /></button><div />
              </div>
              <div className="flex items-center gap-2 mt-2">
                <button className="flex-1 p-1.5 rounded" style={{ backgroundColor: 'var(--input-bg)' }}><ZoomIn className="w-3 h-3 mx-auto" style={{ color: 'var(--text-secondary)' }} /></button>
                <button className="flex-1 p-1.5 rounded" style={{ backgroundColor: 'var(--input-bg)' }}><ZoomOut className="w-3 h-3 mx-auto" style={{ color: 'var(--text-secondary)' }} /></button>
              </div>
              <div className="mt-2 space-y-1">
                {(camera.hardware?.ptzCapabilities?.presets || []).slice(0, 4).map(p => (
                  <button key={p.presetId} className="w-full text-left px-2 py-1 text-[10px] rounded" style={{ backgroundColor: 'var(--input-bg)', color: 'var(--text-muted)' }}>{p.presetName}</button>
                ))}
              </div>
            </div>
          )}
          <div className="rounded-lg p-3" style={card()}>
            <h3 className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Camera Info</h3>
            <div className="space-y-1 text-[10px]">
              {[['IP', camera.connection?.ipAddress], ['FPS', camera.status?.currentFps || '-'], ['Bitrate', camera.status?.currentBitrate ? `${camera.status.currentBitrate} kbps` : '-'], ['Latency', camera.status?.latencyMs ? `${camera.status.latencyMs}ms` : '-'], ['Uptime', camera.status?.uptime ? `${camera.status.uptime}%` : '-']].map(([k, v]) => (
                <div key={k} className="flex justify-between"><span style={{ color: 'var(--text-muted)' }}>{k}</span><span style={{ color: 'var(--text-secondary)' }}>{v}</span></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════ CAMERA MAP TAB ═══════════════════ */

function CameraMapTab({ cameras }) {
  const areaLayout = {
    AREA_1: { x: 20, y: 15, w: 25, h: 30, label: 'Area 1 — Process Units', color: '#3b82f6' },
    AREA_2: { x: 55, y: 10, w: 22, h: 25, label: 'Area 2 — Tank Farm', color: '#f59e0b' },
    AREA_3: { x: 20, y: 55, w: 25, h: 25, label: 'Area 3 — Utilities', color: '#10b981' },
    MAIN_GATE: { x: 3, y: 40, w: 12, h: 20, label: 'Main Gate', color: '#ef4444' },
    STORAGE_YARD: { x: 55, y: 45, w: 22, h: 20, label: 'Storage Yard', color: '#143A6B' },
    PERIMETER: { x: 82, y: 15, w: 15, h: 60, label: 'Perimeter', color: '#6b7280' },
    OFFICE: { x: 55, y: 72, w: 18, h: 18, label: 'Site Office', color: '#06b6d4' },
  };
  return (
    <div className="space-y-3" data-testid="camera-map-tab">
      <div className="flex items-center gap-3 text-xs" style={{ color: 'var(--text-muted)' }}>
        <Map className="w-4 h-4" /><span>2D Site Plan — Camera positions by area ({cameras.length} cameras)</span>
      </div>
      <div className="relative rounded-lg overflow-hidden" style={{ ...card(), height: 520 }}>
        <div className="absolute inset-3 rounded-lg" style={{ border: '2px dashed var(--border-color)' }} />
        <div className="absolute top-1 left-4 text-[10px] font-mono" style={{ color: 'var(--text-muted)' }}>N &#8593;</div>
        {Object.entries(areaLayout).map(([code, layout]) => {
          const areaCams = cameras.filter(c => c.area === code);
          const online = areaCams.filter(c => c.status?.connectionStatus === 'ONLINE').length;
          return (
            <div key={code} className="absolute rounded-lg transition-all group" style={{ left: `${layout.x}%`, top: `${layout.y}%`, width: `${layout.w}%`, height: `${layout.h}%`, borderColor: layout.color + '40', backgroundColor: layout.color + '08', border: `1px solid ${layout.color}40` }}>
              <div className="p-2">
                <p className="text-[10px] font-semibold" style={{ color: layout.color }}>{layout.label}</p>
                <p className="text-[9px]" style={{ color: 'var(--text-muted)' }}>{areaCams.length} cameras &bull; {online} online</p>
              </div>
              <div className="flex flex-wrap gap-1.5 px-2">
                {areaCams.map(cam => (
                  <div key={cam.cameraCode} className="relative group/cam" title={`${cam.cameraCode}: ${cam.cameraName}`}>
                    <div className="w-3.5 h-3.5 rounded-full cursor-pointer" style={{ backgroundColor: STATUS_DOT[cam.status?.connectionStatus] || '#64748b', border: '2px solid rgba(0,0,0,0.3)', boxShadow: `0 0 6px ${STATUS_DOT[cam.status?.connectionStatus] || '#64748b'}40` }} />
                    {cam.aiConfig?.aiEnabled && <Zap className="absolute -top-1 -right-1 w-2 h-2" style={{ color: '#06b6d4' }} />}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover/cam:block z-10">
                      <div className="rounded px-2 py-1 text-[10px] whitespace-nowrap" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                        <p className="font-medium" style={{ color: 'var(--text-primary)' }}>{cam.cameraCode}</p>
                        <p style={{ color: 'var(--text-muted)' }}>{cam.cameraName}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ═══════════════════ SAFETY EVENTS TAB ═══════════════════ */

function SafetyEventsTab({ events, eventFilter, setEventFilter, areaFilter, setAreaFilter, areas, selectedEvent, setSelectedEvent, acknowledgeEvent, markFalsePositive, createHSEIncident, eventsSummary }) {
  const detTypes = [...new Set(events.map(e => e.detection?.detectionType).filter(Boolean))];
  return (
    <div className="space-y-3" data-testid="safety-events-tab">
      {eventsSummary && (
        <div className="grid grid-cols-6 gap-2">
          {[
            { label: 'Total Events', value: eventsSummary.totalAll, color: 'var(--text-primary)' },
            { label: 'Today', value: eventsSummary.totalToday, color: '#06b6d4' },
            { label: 'Critical Today', value: eventsSummary.criticalToday, color: '#ef4444' },
            { label: 'High Today', value: eventsSummary.highToday, color: '#f97316' },
            { label: 'Unacknowledged', value: eventsSummary.newUnacknowledged, color: '#eab308' },
            { label: 'FP Rate', value: `${eventsSummary.falsePositiveRate}%`, color: 'var(--text-secondary)' },
          ].map(kpi => (
            <div key={kpi.label} className="rounded-lg p-3 text-center" style={card()}>
              <p className="text-lg font-bold" style={{ color: kpi.color }}>{typeof kpi.value === 'number' ? kpi.value.toLocaleString('en-IN') : kpi.value}</p>
              <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>{kpi.label}</p>
            </div>
          ))}
        </div>
      )}
      {/* Filters */}
      <div className="flex items-center gap-2 rounded-lg px-3 py-2" style={card()}>
        <Filter className="w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
        {[
          { val: eventFilter.severity, set: v => setEventFilter(p => ({ ...p, severity: v })), opts: ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'], ph: 'All Severities' },
          { val: eventFilter.status, set: v => setEventFilter(p => ({ ...p, status: v })), opts: ['NEW', 'ACKNOWLEDGED', 'ACTION_TAKEN', 'FALSE_POSITIVE', 'CLOSED'], ph: 'All Statuses' },
          { val: eventFilter.type, set: v => setEventFilter(p => ({ ...p, type: v })), opts: detTypes, ph: 'All Types' },
          { val: areaFilter, set: setAreaFilter, opts: areas, ph: 'All Areas' },
        ].map((f, i) => (
          <select key={i} value={f.val} onChange={e => f.set(e.target.value)} className="text-xs rounded px-2 py-1" style={inp()}>
            <option value="">{f.ph}</option>
            {f.opts.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
          </select>
        ))}
        <span className="ml-auto text-xs" style={{ color: 'var(--text-muted)' }}>{events.length} events</span>
      </div>
      {/* Table + Detail */}
      <div className="grid grid-cols-5 gap-3">
        <div className="col-span-3 rounded-lg overflow-hidden max-h-[460px] overflow-y-auto" style={card()}>
          <table className="w-full text-xs">
            <thead className="sticky top-0" style={{ backgroundColor: 'var(--card-bg-alt)' }}>
              <tr>{['Event #', 'Time', 'Camera', 'Type', 'Severity', 'Conf.', 'Status'].map(h => (
                <th key={h} className="px-2 py-2 text-left text-[10px] font-medium uppercase" style={{ color: 'var(--text-muted)' }}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {events.map(evt => (
                <tr key={evt.eventNumber} onClick={() => setSelectedEvent(evt)} className="cursor-pointer transition-colors" data-testid={`event-row-${evt.eventNumber}`}
                  style={{ borderTop: '1px solid var(--border-color)', backgroundColor: selectedEvent?.eventNumber === evt.eventNumber ? 'rgba(6,182,212,0.08)' : undefined }}>
                  <td className="px-2 py-1.5 font-mono" style={{ color: 'var(--text-secondary)' }}>{evt.eventNumber?.slice(-8)}</td>
                  <td className="px-2 py-1.5" style={{ color: 'var(--text-muted)' }}>{evt.eventTimestamp?.slice(5, 16)}</td>
                  <td className="px-2 py-1.5" style={{ color: 'var(--text-secondary)' }}>{evt.cameraCode}</td>
                  <td className="px-2 py-1.5" style={{ color: 'var(--text-secondary)' }}>{evt.detection?.detectionType?.replace(/_/g, ' ')}</td>
                  <td className="px-2 py-1.5"><span className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ backgroundColor: SEV[evt.detection?.severity]?.bg, color: SEV[evt.detection?.severity]?.text, border: `1px solid ${SEV[evt.detection?.severity]?.border}` }}>{evt.detection?.severity}</span></td>
                  <td className="px-2 py-1.5" style={{ color: 'var(--text-muted)' }}>{((evt.detection?.confidence || 0) * 100).toFixed(0)}%</td>
                  <td className="px-2 py-1.5"><span className="px-1.5 py-0.5 rounded text-[10px]" style={{ backgroundColor: EVT_ST[evt.response?.status]?.bg, color: EVT_ST[evt.response?.status]?.text }}>{evt.response?.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Detail */}
        <div className="col-span-2 rounded-lg p-4 max-h-[460px] overflow-y-auto" style={card()}>
          {selectedEvent ? (
            <div className="space-y-3" data-testid="event-detail-panel">
              <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{selectedEvent.eventNumber}</h3>
              <div className="aspect-video rounded-lg flex items-center justify-center relative overflow-hidden" style={{ border: '1px solid var(--border-color)' }}>
                <img src={FEED_IMAGES[selectedEvent.area] || DEFAULT_FEED} alt="Event snapshot" className="w-full h-full object-cover" style={{ filter: 'brightness(0.8) contrast(1.1)' }} />
                <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/50" />
                {selectedEvent.detection?.detectedObjects?.map((obj, i) => (
                  <div key={i} className="absolute rounded" style={{ left: `${(obj.boundingBox?.x || 0) * 100}%`, top: `${(obj.boundingBox?.y || 0) * 100}%`, width: `${(obj.boundingBox?.width || 0.1) * 100}%`, height: `${(obj.boundingBox?.height || 0.2) * 100}%`, border: '2px solid #ef4444' }}>
                    <span className="absolute -top-4 left-0 text-[8px] px-1 rounded" style={{ backgroundColor: '#ef4444', color: '#fff' }}>{obj.objectType}</span>
                  </div>
                ))}
                <span className="absolute top-2 left-2 px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ backgroundColor: SEV[selectedEvent.detection?.severity]?.bg, color: SEV[selectedEvent.detection?.severity]?.text }}>{selectedEvent.detection?.severity}: {selectedEvent.detection?.detectionType?.replace(/_/g, ' ')}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-[10px]">
                {[['Camera', selectedEvent.cameraCode], ['Area', selectedEvent.area], ['Confidence', `${((selectedEvent.detection?.confidence || 0) * 100).toFixed(1)}%`], ['Persons', `${selectedEvent.detection?.totalPersonsInFrame} (${selectedEvent.detection?.violatingPersonsCount} violating)`], ['Status', selectedEvent.response?.status], ['Processing', `${selectedEvent.processingTimeMs}ms`]].map(([k, v]) => (
                  <div key={k}><span style={{ color: 'var(--text-muted)' }}>{k}: </span><span style={{ color: 'var(--text-secondary)' }}>{v}</span></div>
                ))}
              </div>
              {selectedEvent.response?.actionTaken && (
                <div className="rounded p-2 text-[10px]" style={{ backgroundColor: 'rgba(16,185,129,0.1)' }}>
                  <span style={{ color: '#34d399' }}>Action: </span><span style={{ color: 'var(--text-secondary)' }}>{selectedEvent.response.actionTaken}</span>
                </div>
              )}
              <div className="flex flex-wrap gap-1.5 pt-1">
                {selectedEvent.response?.status === 'NEW' && (
                  <Button size="sm" className="text-[10px] h-7" style={{ backgroundColor: '#2563eb' }} onClick={() => acknowledgeEvent(selectedEvent.eventNumber)} data-testid="ack-event-btn">Acknowledge</Button>
                )}
                <Button size="sm" variant="outline" className="text-[10px] h-7" onClick={() => markFalsePositive(selectedEvent.eventNumber)} data-testid="fp-event-btn">False Positive</Button>
                {!selectedEvent.response?.linkedHSEIncidentNumber && (
                  <Button size="sm" className="text-[10px] h-7" style={{ backgroundColor: '#dc2626' }} onClick={() => createHSEIncident(selectedEvent.eventNumber)} data-testid="create-hse-btn">Create HSE Incident</Button>
                )}
                {selectedEvent.response?.linkedHSEIncidentNumber && (
                  <span className="text-[10px] flex items-center gap-1" style={{ color: '#34d399' }}><CheckCircle className="w-3 h-3" />Linked: {selectedEvent.response.linkedHSEIncidentNumber}</span>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-xs" style={{ color: 'var(--text-muted)' }}>Select an event to view details</div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════ RECORDINGS TAB ═══════════════════ */

function RecordingsTab({ recordings, cameras }) {
  const [filterCam, setFilterCam] = useState('');
  const filtered = filterCam ? recordings.filter(r => r.cameraCode === filterCam) : recordings;
  return (
    <div className="space-y-3" data-testid="recordings-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><HardDrive className="w-4 h-4" style={{ color: 'var(--text-muted)' }} /><span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{recordings.length} recordings</span></div>
        <select value={filterCam} onChange={e => setFilterCam(e.target.value)} className="text-xs rounded px-2 py-1" style={inp()}>
          <option value="">All Cameras</option>
          {cameras.map(c => <option key={c.cameraCode} value={c.cameraCode}>{c.cameraCode}</option>)}
        </select>
      </div>
      <div className="rounded-lg overflow-hidden max-h-[480px] overflow-y-auto" style={card()}>
        <table className="w-full text-xs">
          <thead className="sticky top-0" style={{ backgroundColor: 'var(--card-bg-alt)' }}>
            <tr>{['Camera', 'Type', 'Start', 'End', 'Duration', 'Size', 'Format', 'Preserved'].map(h => (
              <th key={h} className="px-3 py-2 text-left text-[10px] font-medium uppercase" style={{ color: 'var(--text-muted)' }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {filtered.map((rec, i) => (
              <tr key={i} style={{ borderTop: '1px solid var(--border-color)' }}>
                <td className="px-3 py-2 font-mono" style={{ color: 'var(--text-secondary)' }}>{rec.cameraCode}</td>
                <td className="px-3 py-2"><span className="px-1.5 py-0.5 rounded text-[10px]" style={{ backgroundColor: 'var(--input-bg)', color: 'var(--text-secondary)' }}>{rec.recordingType?.replace(/_/g, ' ')}</span></td>
                <td className="px-3 py-2" style={{ color: 'var(--text-muted)' }}>{rec.startTime?.slice(0, 16)}</td>
                <td className="px-3 py-2" style={{ color: 'var(--text-muted)' }}>{rec.endTime?.slice(11, 16) || '-'}</td>
                <td className="px-3 py-2" style={{ color: 'var(--text-secondary)' }}>{rec.durationSeconds ? `${Math.round(rec.durationSeconds / 3600)}h` : '-'}</td>
                <td className="px-3 py-2" style={{ color: 'var(--text-muted)' }}>{rec.fileSizeMB ? `${rec.fileSizeMB.toLocaleString('en-IN')} MB` : '-'}</td>
                <td className="px-3 py-2" style={{ color: 'var(--text-muted)' }}>{rec.format}</td>
                <td className="px-3 py-2">{rec.isPreserved ? <CheckCircle className="w-3 h-3" style={{ color: '#34d399' }} /> : <span style={{ color: 'var(--text-muted)' }}>-</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ═══════════════════ CAMERA CONFIG TAB ═══════════════════ */

function CameraConfigTab({ cameras, projectId, fetchAll, showConfigModal, setShowConfigModal, editingCamera, setEditingCamera }) {
  const del = async (code) => { if (!window.confirm(`Decommission ${code}?`)) return; try { await api.delete(`/projects/${projectId}/sitemon/cameras/${code}`); toast.success('Decommissioned'); fetchAll(); } catch { toast.error('Failed'); } };
  const test = async (code) => { try { const r = await api.post(`/projects/${projectId}/sitemon/cameras/${code}/test`); if (r.data.success) toast.success(`${code}: ${r.data.resolution}, ${r.data.codec}, ${r.data.fps}fps`); else toast.error(`${code}: ${r.data.message}`); fetchAll(); } catch { toast.error('Test failed'); } };

  return (
    <div className="space-y-3" data-testid="camera-config-tab">
      <div className="flex items-center justify-between">
        <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{cameras.length} cameras registered</span>
        <PermissionGate module="SITE_MONITORING" action="create">
          <Button size="sm" onClick={() => { setEditingCamera(null); setShowConfigModal(true); }} data-testid="add-camera-btn" className="text-xs" style={{ backgroundColor: '#0891b2' }}><Plus className="w-3 h-3 mr-1" />Add Camera</Button>
        </PermissionGate>
      </div>
      <div className="rounded-lg overflow-hidden max-h-[480px] overflow-y-auto" style={card()}>
        <table className="w-full text-xs">
          <thead className="sticky top-0" style={{ backgroundColor: 'var(--card-bg-alt)' }}>
            <tr>{['Code', 'Name', 'Area', 'Group', 'IP', 'Type', 'Res.', 'Status', 'AI', 'Rec', 'Actions'].map(h => (
              <th key={h} className="px-2 py-2 text-left text-[10px] font-medium uppercase" style={{ color: 'var(--text-muted)' }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {cameras.map(cam => (
              <tr key={cam.cameraCode} style={{ borderTop: '1px solid var(--border-color)' }} data-testid={`config-row-${cam.cameraCode}`}>
                <td className="px-2 py-1.5 font-mono" style={{ color: '#06b6d4' }}>{cam.cameraCode}</td>
                <td className="px-2 py-1.5 max-w-[140px] truncate" style={{ color: 'var(--text-secondary)' }}>{cam.cameraName}</td>
                <td className="px-2 py-1.5" style={{ color: 'var(--text-muted)' }}>{cam.area}</td>
                <td className="px-2 py-1.5"><span className="px-1.5 py-0.5 rounded text-[10px]" style={{ backgroundColor: 'var(--input-bg)', color: 'var(--text-secondary)' }}>{cam.cameraGroup}</span></td>
                <td className="px-2 py-1.5 font-mono" style={{ color: 'var(--text-muted)' }}>{cam.connection?.ipAddress}</td>
                <td className="px-2 py-1.5" style={{ color: 'var(--text-muted)' }}>{cam.hardware?.cameraType?.replace(/_/g, ' ')}</td>
                <td className="px-2 py-1.5" style={{ color: 'var(--text-secondary)' }}>{cam.hardware?.resolution}</td>
                <td className="px-2 py-1.5"><span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_DOT[cam.status?.connectionStatus] }} /><span style={{ color: 'var(--text-secondary)' }}>{cam.status?.connectionStatus}</span></span></td>
                <td className="px-2 py-1.5">{cam.aiConfig?.aiEnabled ? <Zap className="w-3 h-3" style={{ color: '#06b6d4' }} /> : <span style={{ color: 'var(--text-muted)' }}>-</span>}</td>
                <td className="px-2 py-1.5" style={{ color: cam.recording?.recordingEnabled ? '#34d399' : 'var(--text-muted)' }}>{cam.recording?.recordingEnabled ? 'ON' : 'OFF'}</td>
                <td className="px-2 py-1.5">
                  <div className="flex items-center gap-1">
                    <button onClick={() => test(cam.cameraCode)} className="p-1 rounded hover:opacity-80" title="Test"><Wifi className="w-3 h-3" style={{ color: 'var(--text-muted)' }} /></button>
                    <button onClick={() => { setEditingCamera(cam); setShowConfigModal(true); }} className="p-1 rounded hover:opacity-80" title="Edit"><Edit2 className="w-3 h-3" style={{ color: 'var(--text-muted)' }} /></button>
                    <button onClick={() => del(cam.cameraCode)} className="p-1 rounded hover:opacity-80" title="Decommission"><Trash2 className="w-3 h-3" style={{ color: '#f87171' }} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showConfigModal && <CameraConfigModal projectId={projectId} camera={editingCamera} onClose={() => setShowConfigModal(false)} onSaved={() => { setShowConfigModal(false); fetchAll(); }} />}
    </div>
  );
}

function CameraConfigModal({ projectId, camera, onClose, onSaved }) {
  const isEdit = !!camera;
  const [form, setForm] = useState({
    cameraCode: camera?.cameraCode || '', cameraName: camera?.cameraName || '', cameraDescription: camera?.cameraDescription || '',
    area: camera?.area || 'AREA_1', zone: camera?.zone || '', cameraGroup: camera?.cameraGroup || 'WORK_FRONT', discipline: camera?.discipline || 'GENERAL',
    ipAddress: camera?.connection?.ipAddress || '', port: camera?.connection?.port || 554, protocol: camera?.connection?.protocol || 'RTSP',
    username: camera?.connection?.username || 'admin', password: '',
    manufacturer: camera?.hardware?.manufacturer || 'Hikvision', model: camera?.hardware?.model || '', cameraType: camera?.hardware?.cameraType || 'FIXED_BULLET',
    resolution: camera?.hardware?.resolution || '1080P', isPTZ: camera?.hardware?.isPTZ || false, nightVision: camera?.hardware?.nightVision || true,
    recordingEnabled: camera?.recording?.recordingEnabled ?? true, recordingMode: camera?.recording?.recordingMode || 'CONTINUOUS', retentionDays: camera?.recording?.retentionDays || 30,
    gpsLat: camera?.location?.gpsLatitude || '', gpsLng: camera?.location?.gpsLongitude || '', elevation: camera?.location?.elevation || '', mountingType: camera?.location?.mountingType || 'POLE',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.cameraCode || !form.cameraName) { toast.error('Code and name required'); return; }
    setSaving(true);
    try {
      const payload = { cameraCode: form.cameraCode, cameraName: form.cameraName, cameraDescription: form.cameraDescription, area: form.area, zone: form.zone, cameraGroup: form.cameraGroup, discipline: form.discipline,
        connection: { protocol: form.protocol, streamUrl: `rtsp://${form.username}:${form.password || 'pass'}@${form.ipAddress}:${form.port}/stream1`, ipAddress: form.ipAddress, port: form.port, username: form.username, onvifEnabled: form.isPTZ },
        hardware: { manufacturer: form.manufacturer, model: form.model, cameraType: form.cameraType, resolution: form.resolution, isPTZ: form.isPTZ, nightVision: form.nightVision, nightVisionType: 'IR_LED', codec: 'H.265', fps: 25 },
        recording: { recordingEnabled: form.recordingEnabled, recordingMode: form.recordingMode, retentionDays: form.retentionDays, storageLocation: 'NVR_LOCAL' },
        location: { gpsLatitude: parseFloat(form.gpsLat) || null, gpsLongitude: parseFloat(form.gpsLng) || null, elevation: parseFloat(form.elevation) || null, mountingType: form.mountingType },
      };
      if (isEdit) await api.put(`/projects/${projectId}/sitemon/cameras/${form.cameraCode}`, payload);
      else await api.post(`/projects/${projectId}/sitemon/cameras`, payload);
      toast.success(isEdit ? 'Updated' : 'Registered');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const F = (label, field, type = 'text', opts) => (
    <div>
      <label className="text-[10px] block mb-0.5" style={{ color: 'var(--text-muted)' }}>{label}</label>
      {opts ? (
        <select value={form[field]} onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))} className="w-full text-xs rounded px-2 py-1.5" style={inp()}>
          {opts.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
        </select>
      ) : (
        <input type={type} value={form[field]} onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))} className="w-full text-xs rounded px-2 py-1.5" style={inp()} />
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} onClick={onClose}>
      <div className="rounded-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-5" style={{ backgroundColor: 'var(--surface-bg, var(--card-bg))', border: '1px solid var(--border-color)' }} onClick={e => e.stopPropagation()} data-testid="camera-config-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{isEdit ? 'Edit' : 'Register'} Camera</h2>
          <button onClick={onClose}><X className="w-4 h-4" style={{ color: 'var(--text-muted)' }} /></button>
        </div>
        {[
          { title: 'Basic Information', fields: () => <div className="grid grid-cols-3 gap-2">{F('Camera Code *', 'cameraCode')}{F('Camera Name *', 'cameraName')}{F('Description', 'cameraDescription')}{F('Area', 'area', 'text', ['AREA_1', 'AREA_2', 'AREA_3', 'MAIN_GATE', 'STORAGE_YARD', 'PERIMETER', 'OFFICE'])}{F('Zone', 'zone')}{F('Group', 'cameraGroup', 'text', ['WORK_FRONT', 'SAFETY_CRITICAL', 'GATE', 'PERIMETER', 'STORAGE_YARD', 'OFFICE'])}</div> },
          { title: 'Connection', fields: () => <div className="grid grid-cols-4 gap-2">{F('Protocol', 'protocol', 'text', ['RTSP', 'ONVIF', 'HTTP_MJPEG', 'HLS'])}{F('IP Address', 'ipAddress')}{F('Port', 'port', 'number')}{F('Username', 'username')}{F('Password', 'password', 'password')}</div> },
          { title: 'Hardware', fields: () => <><div className="grid grid-cols-4 gap-2">{F('Manufacturer', 'manufacturer', 'text', ['Hikvision', 'Dahua', 'Axis', 'Hanwha', 'Bosch'])}{F('Model', 'model')}{F('Type', 'cameraType', 'text', ['FIXED_BULLET', 'FIXED_DOME', 'PTZ_DOME', 'PTZ_BULLET', 'FISHEYE_360', 'THERMAL'])}{F('Resolution', 'resolution', 'text', ['1080P', '2K', '4K', '4MP', '8MP'])}</div><div className="flex gap-4 mt-2"><label className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--text-secondary)' }}><input type="checkbox" checked={form.isPTZ} onChange={e => setForm(p => ({ ...p, isPTZ: e.target.checked }))} className="rounded" />PTZ</label><label className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--text-secondary)' }}><input type="checkbox" checked={form.nightVision} onChange={e => setForm(p => ({ ...p, nightVision: e.target.checked }))} className="rounded" />Night Vision</label></div></> },
          { title: 'Recording', fields: () => <div className="grid grid-cols-3 gap-2"><label className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--text-secondary)' }}><input type="checkbox" checked={form.recordingEnabled} onChange={e => setForm(p => ({ ...p, recordingEnabled: e.target.checked }))} className="rounded" />Enabled</label>{F('Mode', 'recordingMode', 'text', ['CONTINUOUS', 'MOTION_TRIGGERED', 'SCHEDULED'])}{F('Retention (days)', 'retentionDays', 'number')}</div> },
        ].map(s => (
          <div key={s.title} className="mb-3 p-3 rounded-lg" style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--border-color)' }}>
            <p className="text-[10px] font-semibold uppercase mb-2" style={{ color: '#06b6d4' }}>{s.title}</p>
            {s.fields()}
          </div>
        ))}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} style={{ backgroundColor: '#0891b2' }}>{saving ? 'Saving...' : isEdit ? 'Update' : 'Register'}</Button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════ AI SETTINGS TAB ═══════════════════ */

function AISettingsTab({ cameras, projectId, fetchAll }) {
  const [selectedCam, setSelectedCam] = useState(null);
  const toggle = async (cam) => {
    try { await api.put(`/projects/${projectId}/sitemon/cameras/${cam.cameraCode}/ai-config`, { ...cam.aiConfig, aiEnabled: !cam.aiConfig.aiEnabled }); toast.success(`AI ${!cam.aiConfig.aiEnabled ? 'enabled' : 'disabled'}`); fetchAll(); } catch { toast.error('Failed'); }
  };
  const updateDet = async (cam, model, val) => {
    try { await api.put(`/projects/${projectId}/sitemon/cameras/${cam.cameraCode}/ai-config`, { ...cam.aiConfig, detectionModels: { ...cam.aiConfig.detectionModels, [model]: val } }); fetchAll(); } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-3" data-testid="ai-settings-tab">
      <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-muted)' }}>
        <Zap className="w-4 h-4" style={{ color: '#06b6d4' }} /><span>AI Safety Detection — {cameras.filter(c => c.aiConfig?.aiEnabled).length}/{cameras.length} active</span>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg max-h-[460px] overflow-y-auto" style={card()}>
          {cameras.map(cam => (
            <div key={cam.cameraCode} onClick={() => setSelectedCam(cam)} className="flex items-center justify-between px-3 py-2 cursor-pointer transition-colors"
              style={{ borderBottom: '1px solid var(--border-color)', backgroundColor: selectedCam?.cameraCode === cam.cameraCode ? 'rgba(6,182,212,0.08)' : undefined }}>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-mono" style={{ color: 'var(--text-primary)' }}>{cam.cameraCode}</p>
                <p className="text-[10px] truncate" style={{ color: 'var(--text-muted)' }}>{cam.cameraName}</p>
              </div>
              <button onClick={e => { e.stopPropagation(); toggle(cam); }} className="px-2 py-0.5 rounded text-[10px] font-medium"
                style={cam.aiConfig?.aiEnabled ? { backgroundColor: 'rgba(6,182,212,0.15)', color: '#22d3ee' } : { backgroundColor: 'var(--input-bg)', color: 'var(--text-muted)' }}>{cam.aiConfig?.aiEnabled ? 'ON' : 'OFF'}</button>
            </div>
          ))}
        </div>
        <div className="col-span-2 rounded-lg p-4 max-h-[460px] overflow-y-auto" style={card()}>
          {selectedCam ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{selectedCam.cameraCode} — AI Config</h3>
                <span className="px-2 py-0.5 rounded text-xs" style={selectedCam.aiConfig?.aiEnabled ? { backgroundColor: 'rgba(6,182,212,0.15)', color: '#22d3ee' } : { backgroundColor: 'var(--input-bg)', color: 'var(--text-muted)' }}>{selectedCam.aiConfig?.aiEnabled ? 'Active' : 'Disabled'}</span>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase mb-2" style={{ color: '#06b6d4' }}>Detection Models</p>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(selectedCam.aiConfig?.detectionModels || {}).map(([key, val]) => (
                    <label key={key} className="flex items-center gap-2 text-[10px] p-1.5 rounded" style={{ backgroundColor: 'var(--input-bg)', color: 'var(--text-secondary)' }}>
                      <input type="checkbox" checked={val} onChange={e => updateDet(selectedCam, key, e.target.checked)} className="rounded" />
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase mb-2" style={{ color: '#06b6d4' }}>Confidence: {((selectedCam.aiConfig?.confidenceThreshold || 0.7) * 100).toFixed(0)}%</p>
                <input type="range" min="50" max="95" value={(selectedCam.aiConfig?.confidenceThreshold || 0.7) * 100} readOnly className="w-full accent-cyan-500" />
                <div className="flex justify-between text-[9px]" style={{ color: 'var(--text-muted)' }}><span>50% (More)</span><span>95% (Fewer)</span></div>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase mb-2" style={{ color: '#06b6d4' }}>Processing</p>
                <div className="grid grid-cols-3 gap-2 text-[10px]">
                  {[['Mode', selectedCam.aiConfig?.processingMode], ['Sample Rate', `${selectedCam.aiConfig?.sampleRateSeconds}s`], ['Cooldown', `${selectedCam.aiConfig?.alertCooldownSeconds}s`]].map(([k, v]) => (
                    <div key={k} className="p-2 rounded" style={{ backgroundColor: 'var(--input-bg)' }}><span style={{ color: 'var(--text-muted)' }}>{k}: </span><span style={{ color: 'var(--text-secondary)' }}>{v}</span></div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-xs" style={{ color: 'var(--text-muted)' }}>Select a camera to configure AI</div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════ ANALYTICS TAB ═══════════════════ */

function AnalyticsTab({ analytics, eventsSummary, health }) {
  const byType = eventsSummary?.byType || [];
  const topCameras = eventsSummary?.topCameras || [];
  const dailyTrend = analytics?.dailyTrend || [];
  const complianceTrend = analytics?.complianceTrend || [];
  const maxDaily = Math.max(...dailyTrend.map(d => d.count), 1);
  const avgCompliance = complianceTrend.length > 0 ? (complianceTrend.reduce((s, c) => s + c.complianceRate, 0) / complianceTrend.length).toFixed(1) : 'N/A';
  const TC = ['#ef4444', '#f97316', '#eab308', '#06b6d4', '#143A6B', '#ec4899', '#10b981', '#6366f1'];
  const totalByType = byType.reduce((s, t) => s + t.count, 0) || 1;

  return (
    <div className="space-y-4" data-testid="analytics-tab">
      <div className="grid grid-cols-6 gap-2">
        {[
          { label: 'Cameras', value: health?.total || 0, sub: `${health?.online || 0} online`, icon: Camera, color: 'var(--text-primary)' },
          { label: 'Online Rate', value: `${health?.total ? ((health.online / health.total) * 100).toFixed(1) : 0}%`, sub: `${health?.offline || 0} offline`, icon: Wifi, color: '#10b981' },
          { label: 'AI Active', value: health?.aiActive || 0, sub: `of ${health?.total || 0}`, icon: Zap, color: '#06b6d4' },
          { label: 'Events (30d)', value: eventsSummary?.totalAll?.toLocaleString('en-IN') || '0', sub: `${eventsSummary?.criticalToday || 0} critical today`, icon: Shield, color: '#ef4444' },
          { label: 'Avg Compliance', value: avgCompliance !== 'N/A' ? `${avgCompliance}%` : 'N/A', sub: 'last 30 days', icon: CheckCircle, color: '#10b981' },
          { label: 'FP Rate', value: `${eventsSummary?.falsePositiveRate || 0}%`, sub: 'false positives', icon: XCircle, color: 'var(--text-muted)' },
        ].map(kpi => (
          <div key={kpi.label} className="rounded-lg p-3" style={card()}>
            <div className="flex items-center gap-2 mb-1"><kpi.icon className="w-3.5 h-3.5" style={{ color: kpi.color }} /><span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{kpi.label}</span></div>
            <p className="text-lg font-bold" style={{ color: kpi.color }}>{kpi.value}</p>
            <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{kpi.sub}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg p-4" style={card()}>
          <h3 className="text-xs font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>Violations by Type</h3>
          <div className="space-y-2">
            {byType.map((t, i) => (
              <div key={t.type} className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: TC[i % TC.length] }} />
                <span className="flex-1 text-[10px]" style={{ color: 'var(--text-secondary)' }}>{t.type?.replace(/_/g, ' ')}</span>
                <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{t.count}</span>
                <div className="w-16 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--input-bg)' }}>
                  <div className="h-full rounded-full" style={{ width: `${(t.count / totalByType) * 100}%`, backgroundColor: TC[i % TC.length] }} />
                </div>
                <span className="text-[10px] w-8 text-right" style={{ color: 'var(--text-muted)' }}>{((t.count / totalByType) * 100).toFixed(0)}%</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-lg p-4" style={card()}>
          <h3 className="text-xs font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>Daily Violations (30d)</h3>
          <div className="flex items-end gap-px h-32">
            {dailyTrend.slice(-30).map(d => (
              <div key={d.date} className="flex-1 flex flex-col items-center justify-end group" title={`${d.date}: ${d.count}`}>
                <div className="w-full rounded-t transition-all group-hover:opacity-80" style={{ height: `${(d.count / maxDaily) * 100}%`, minHeight: d.count > 0 ? 2 : 0, backgroundColor: 'rgba(239,68,68,0.6)' }} />
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-1"><span className="text-[9px]" style={{ color: 'var(--text-muted)' }}>{dailyTrend[0]?.date?.slice(5)}</span><span className="text-[9px]" style={{ color: 'var(--text-muted)' }}>{dailyTrend[dailyTrend.length - 1]?.date?.slice(5)}</span></div>
        </div>
        <div className="rounded-lg p-4" style={card()}>
          <h3 className="text-xs font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>Top Violation Cameras</h3>
          <div className="space-y-2">
            {topCameras.slice(0, 8).map((tc, i) => (
              <div key={tc.cameraCode} className="flex items-center gap-2">
                <span className="text-[10px] w-3" style={{ color: 'var(--text-muted)' }}>{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-mono truncate" style={{ color: 'var(--text-secondary)' }}>{tc.cameraCode}</p>
                  <p className="text-[9px] truncate" style={{ color: 'var(--text-muted)' }}>{tc.area}</p>
                </div>
                <span className="text-xs font-bold" style={{ color: '#ef4444' }}>{tc.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {complianceTrend.length > 0 && (
        <div className="rounded-lg p-4" style={card()}>
          <h3 className="text-xs font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>Compliance Rate Trend (30d)</h3>
          <div className="flex items-end gap-px h-24">
            {complianceTrend.slice(-30).map(c => (
              <div key={c.date} className="flex-1 group" title={`${c.date}: ${c.complianceRate}%`}>
                <div className="w-full rounded-t transition-all group-hover:opacity-80" style={{ height: `${c.complianceRate}%`, backgroundColor: 'rgba(16,185,129,0.5)' }} />
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-[9px]" style={{ color: 'var(--text-muted)' }}>{complianceTrend[0]?.date?.slice(5)}</span>
            <span className="text-[9px] font-medium" style={{ color: 'var(--text-secondary)' }}>Avg: {avgCompliance}%</span>
            <span className="text-[9px]" style={{ color: 'var(--text-muted)' }}>{complianceTrend[complianceTrend.length - 1]?.date?.slice(5)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
